import React, { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar1 } from 'lucide-react';
import { z } from 'zod';

import { Button, CustomSelect, RichTextEditor, Toggle } from '@/components/ui';
import Calendar from '@/components/ui/Calendar/Calendar';
import Input from '@/components/ui/Input/Input';
import { Heading, Paragraph } from '@/components/ui/Typography';
import { useCreateMemberCountry, useMemberCountry, useUpdateMemberCountry } from '@/lib/api/hooks/memberCountry';
import { useCountries, useRegion } from '@/lib/api/hooks/region';
import { MemberCountryFormSchema, type Country } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';
import officialLanguageOptions from '@/utils/constants/officialLanguageOptions';

type MemberCountryFormInput = z.input<typeof MemberCountryFormSchema>;
type MemberCountryFormOutput = z.output<typeof MemberCountryFormSchema>;

const DEFAULT_FORM_VALUES: MemberCountryFormInput = {
  countryId: '',
  isBio: true,
  title: '',
  description: '',
  officialLanguage: '',
  dateJoined: '',
  additionalLinks: '',
};

const SectionHeader = ({ index, title, subtitle }: { index: number; title: string; subtitle?: string }) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#681628] text-xs font-semibold text-white">
      {index}
    </div>
    <div>
      <Heading level={4} className="text-lg font-semibold text-gray-900">
        {title}
      </Heading>
      {subtitle ? <Paragraph className="text-sm text-gray-500">{subtitle}</Paragraph> : null}
    </div>
  </div>
);

const toDateString = (value: Date | string | null | undefined): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MemberCountryEditor: React.FC = () => {
  const { regionId, memberCountryId } = useParams<{ regionId: string; memberCountryId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(memberCountryId);

  const { data: regionData } = useRegion(regionId);
  const regionName = regionData?.data?.regionName || location.state?.regionName || 'Region';
  const regionTag = regionData?.data?.regionTag || location.state?.regionTag;

  const { data: countriesData } = useCountries({ regionTag, page: 1, size: 200 });
  const { data: memberCountryData, isLoading: loadingMemberCountry } = useMemberCountry(memberCountryId);

  const createMutation = useCreateMemberCountry();
  const updateMutation = useUpdateMemberCountry();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<MemberCountryFormInput, unknown, MemberCountryFormOutput>({
    resolver: zodResolver(MemberCountryFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const countryId = watch('countryId');

  const countries = useMemo(() => {
    const countryList = (countriesData?.data?.data || []) as Country[];

    return countryList
      .slice()
      .sort((a, b) => a.countryName.localeCompare(b.countryName))
      .map((country) => ({
        value: country.countryId,
        label: country.countryName,
        flagUrl: country.flagUrl,
      }));
  }, [countriesData]);

  const countryNameForBreadcrumb = useMemo(() => {
    const selectedCountry = countries.find((country) => country.value === countryId);
    if (selectedCountry?.label) {
      return selectedCountry.label;
    }

    const countryFromApi = memberCountryData?.data?.countryName;
    if (typeof countryFromApi === 'string' && countryFromApi.trim()) {
      return countryFromApi;
    }

    const countryFromState = location.state?.country?.countryName;
    if (typeof countryFromState === 'string' && countryFromState.trim()) {
      return countryFromState;
    }

    return 'Country';
  }, [countries, countryId, memberCountryData, location.state]);

  useEffect(() => {
    const memberCountry = (memberCountryData?.data || location.state?.country || null) as Record<string, unknown> | null;

    if (isEditMode && memberCountry) {
      const description = String(memberCountry.description || '');
      reset({
        countryId: String(memberCountry.countryId || ''),
        isBio: Boolean(memberCountry.title || memberCountry.description),
        title: String(memberCountry.title || ''),
        description: description,
        officialLanguage: String(memberCountry.officialLanguage || ''),
        dateJoined: toDateString(String(memberCountry.dateJoined || '')),
        additionalLinks: String(memberCountry.additionalLinks || ''),
      });
      return;
    }

    if (!isEditMode) {
      reset(DEFAULT_FORM_VALUES);
    }
  }, [isEditMode, memberCountryData, location.state, reset]);

  const handleGoBack = () => {
    if (isDirty && !isSubmitting) {
      showToast.info('You have unsaved changes. Save before leaving.');
      return;
    }

    navigate(`/admin/member-countries/${regionId}`, {
      state: { regionName, regionTag },
    });
  };

  const onSubmit = async (data: MemberCountryFormOutput) => {
    if (!regionId) {
      showToast.error('Region ID is missing');
      return;
    }

    const payload = {
      ...data,
      regionId,
      status: 'PUBLISH' as const,
    };

    try {
      if (isEditMode && memberCountryId) {
        await updateMutation.mutateAsync({ memberCountryId, data: payload });
        showToast.success('Member country updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        showToast.success('Member country published successfully');
      }

      navigate(`/admin/member-countries/${regionId}`, {
        state: { regionName, regionTag },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save member country';
      showToast.error(message);
    }
  };

  if (loadingMemberCountry && isEditMode) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-10">
        <Loader size="md" variant="wine" message="Loading member country..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white py-2">
        <Button type="button" variant="ghost" onClick={handleGoBack} disabled={isSubmitting} className="!px-0 !py-0">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>

        <Button
          type="button"
          loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
          disabled={!isValid || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {isEditMode ? 'Update Country' : 'Publish Country'}
        </Button>
      </div>

      <div className="mx-auto max-w-5xl space-y-6">

    

      <div className="">
        <Heading level={3} className="mb-1 font-semibold text-[#0F172A]">
          {isEditMode ? 'Edit Member Country' : 'Create Member Country'}
        </Heading>

        <nav className="flex flex-wrap items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => navigate('/admin/member-countries')}
            className="text-wine-100 hover:text-wine-200 hover:underline"
            disabled={isSubmitting}
          >
            Member Countries
          </button>

          <span className="text-gray-400">/</span>

          <button
            type="button"
            onClick={() =>
              navigate(`/admin/member-countries/${regionId}`, {
                state: { regionName, regionTag },
              })
            }
            className="text-wine-100 hover:text-wine-200 hover:underline"
            disabled={isSubmitting}
          >
            {regionName}
          </button>

          {isEditMode ? (
            <>
              <span className="text-gray-400">/</span>
              <button
                type="button"
                onClick={() =>
                  navigate(`/admin/member-countries/${regionId}/${memberCountryId}`, {
                    state: { regionName, regionTag },
                  })
                }
                className="text-wine-100 hover:text-wine-200 hover:underline"
                disabled={isSubmitting || !memberCountryId}
              >
                {countryNameForBreadcrumb}
              </button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">Edit</span>
            </>
          ) : (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">Create</span>
            </>
          )}
        </nav>
      </div>

      <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <SectionHeader index={1} title="Basic Information" />

          <div className="grid grid-cols-1 items-end gap-6 md:grid-cols-2">
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  id="countryId"
                  label="Country"
                  required
                  options={countries}
                  value={field.value}
                  onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                  onBlur={field.onBlur}
                  placeholder="Select Country"
                  error={errors.countryId?.message ? String(errors.countryId.message) : undefined}
                />
              )}
            />

    <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  label="Title"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Enter a descriptive title for the country"
                  $marginbottom="0"
                  error={errors.title?.message ? String(errors.title.message) : undefined}
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="isBio"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Add Bio"
                
                  disabled={isSubmitting}
                  className="justify-start gap-6"
                />
              )}
            />

        
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <SectionHeader
            index={3}
            title="Country Description"
            subtitle="Provide a detailed overview about the country."
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Write the country description here..."
                disabled={isSubmitting}
                showExtendedToolbar={true}
                error={errors.description?.message ? String(errors.description.message) : undefined}
              />
            )}
          />
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <SectionHeader
            index={4}
            title="Additional Information"
          />

          <div className="grid grid-cols-1 items-end gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
                <Controller
              name="additionalLinks"
              control={control}
              render={({ field }) => (
                <Input
                  label="Link"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="country link (e.g. https://en.wikipedia.org/wiki/Nigeria)"
                  $marginbottom="0"
                  error={errors.additionalLinks?.message ? String(errors.additionalLinks.message) : undefined}
                  disabled={isSubmitting}
                />
              )}
            />
            </div>
            <Controller
              name="dateJoined"
              control={control}
              render={({ field }) => (
                <Calendar
                  label="Membership Date"
                  selectedDate={field.value ? new Date(field.value) : null}
                  onChange={(date) => field.onChange(toDateString(date))}
                  placeholder="Select date"
                  error={errors.dateJoined?.message ? String(errors.dateJoined.message) : undefined}
                  $dateFormat="yyyy-MM-dd"
                  $marginbottom="0"
                  icon={<Calendar1 className="h-4 w-4" />}
                  $isDisabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="officialLanguage"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Official Language"
                  value={field.value ?? ''}
                  options={officialLanguageOptions}
                  onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                  onBlur={field.onBlur}
                  placeholder="Select language"
                  error={errors.officialLanguage?.message ? String(errors.officialLanguage.message) : undefined}
                  disabled={isSubmitting}
                />
              )}
            />
          </div>
        </section>
      </form>
        </div>
    </div>
  );
};

export default MemberCountryEditor;
